import { Card, CardBody, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow } from '@heroui/react';
import NextLink from 'next/link';

import { competitorComparisonPath, type CompetitorVsEntry, getCompetitorLinks } from '../../data/competitor-comparisons';
import { getCompetitorPricing } from '../../data/competitor-pricing';
import { isPublishable } from '../../data/pseo/types';
import { useSession } from '../../hooks/useSession';
import { getHeadingId } from '../../utils/storyblok';
import { Section } from '../layout/Storyblok/Section/Section';
import wysiwygStyles from '../layout/Storyblok/Wysiwyg/Wysiwyg.module.scss';
import { formatIsoDate, LastVerifiedBadge } from '../pseo/LastVerifiedBadge';
import { CTASection } from '../shared/CTASection';
import { FAQStructuredData } from '../shared/FAQStructuredData';
import { ComparisonPageLayout } from './ComparisonPageLayout';
import {
  ComparisonLinkList,
  CTA_DESCRIPTION,
  FaqList,
  KreatliPlanList,
  makeHeroBlok,
  SummaryBox,
} from './CompetitorPricingPage';

interface Props {
  entry: CompetitorVsEntry;
  /** `<title>` before " | Kreatli" (≤ 50 characters). */
  documentTitle: string;
  metaDescription: string;
  h1: string;
  heroText: string;
  publishDate: string;
  readTimeMinutes: string;
  coverSrc: string;
  coverAlt: string;
}

/**
 * Shared template for `/comparisons/<a>-vs-<b>`. Facts come from `competitor-comparisons.ts`; starting prices from
 * `competitor-pricing.ts`. Includes a fair "when X is the better choice" section for both tools.
 */
export function CompetitorVsPage({
  entry,
  documentTitle,
  metaDescription,
  h1,
  heroText,
  publishDate,
  readTimeMinutes,
  coverSrc,
  coverAlt,
}: Props) {
  useSession();

  const pricingA = getCompetitorPricing(entry.a);
  const pricingB = getCompetitorPricing(entry.b);
  const nameA = pricingA.vendor;
  const nameB = pricingB.vendor;
  const canonicalPath = competitorComparisonPath(entry);
  const sections = [
    `${nameA} vs ${nameB} at a glance`,
    `When ${nameA} is the better choice`,
    `When ${nameB} is the better choice`,
    'Where Kreatli fits',
    'Related pricing and comparisons',
    'Sources',
    `FAQ: ${nameA} vs ${nameB}`,
  ];
  const tocLinks = sections.map((label) => ({ label, url: `#${getHeadingId(label)}` }));
  const links = getCompetitorLinks([entry.a, entry.b], canonicalPath);
  const notes = [pricingA, pricingB].flatMap((pricing) => {
    if (!isPublishable(pricing)) {
      return [`${pricing.vendor} pricing varies by region and isn't shown as a number here. Check the vendor's site.`];
    }
    return pricing.currency === 'USD' ? [] : [`${pricing.vendor} prices are in ${pricing.currency} and are not converted.`];
  });

  return (
    <>
      <FAQStructuredData faqs={entry.faqs} />
      <ComparisonPageLayout
        documentTitle={documentTitle}
        metaDescription={metaDescription}
        canonicalPath={canonicalPath}
        ogImageUrl={coverSrc}
        ogImageAlt={coverAlt}
        publishDate={publishDate}
        readTimeMinutes={readTimeMinutes}
        breadcrumbLabel={`${nameA} vs ${nameB}`}
        tocLinks={tocLinks}
      >
        <Section blok={makeHeroBlok({ id: entry.slug, title: h1, text: heroText, coverSrc, coverAlt })} />

        <SummaryBox>
          <p>{entry.summary}</p>
          <p className="mt-2">
            Facts checked {formatIsoDate(entry.lastVerified)} on each vendor&apos;s own pages. Kreatli is our product,
            so it&apos;s covered separately below.
          </p>
        </SummaryBox>

        <div className={wysiwygStyles.wysiwyg}>
          <h2 id={getHeadingId(sections[0])}>{sections[0]}</h2>
        </div>

        <Card className="border border-foreground-200 bg-content1/60 backdrop-blur-lg">
          <CardBody className="overflow-x-auto p-2 sm:p-4">
            <Table aria-label={`${nameA} vs ${nameB} comparison`} removeWrapper className="min-w-[680px]">
              <TableHeader>
                <TableColumn> </TableColumn>
                <TableColumn>{nameA}</TableColumn>
                <TableColumn>{nameB}</TableColumn>
              </TableHeader>
              <TableBody>
                {entry.rows.map((row) => (
                  <TableRow key={row.label}>
                    <TableCell className="font-semibold text-foreground-700">{row.label}</TableCell>
                    <TableCell>{row.a}</TableCell>
                    <TableCell>{row.b}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardBody>
        </Card>

        <div className={wysiwygStyles.wysiwyg}>
          <h2 id={getHeadingId(sections[1])}>{sections[1]}</h2>
          <ul>
            {entry.whenAIsBetter.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>

          <hr />

          <h2 id={getHeadingId(sections[2])}>{sections[2]}</h2>
          <ul>
            {entry.whenBIsBetter.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>

          <hr />

          <h2 id={getHeadingId(sections[3])}>{sections[3]}</h2>
          <p>
            If neither {nameA} nor {nameB} fits how your team reviews with clients, here is what Kreatli does and
            doesn&apos;t do:
          </p>
          <ul>
            {entry.whereKreatliFits.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p>Kreatli plans:</p>
          <KreatliPlanList />
          <p>
            See <NextLink href="/platform/review-approval">Kreatli&apos;s video review software</NextLink> for the full
            feature list.
          </p>

          <hr />

          <h2 id={getHeadingId(sections[4])}>{sections[4]}</h2>
          <ComparisonLinkList links={links} />

          <hr />

          <h2 id={getHeadingId(sections[5])}>{sections[5]}</h2>
        </div>

        <LastVerifiedBadge
          lastVerified={entry.lastVerified}
          sources={entry.sourceUrls}
          changelog={entry.changelog}
          notes={notes}
        />

        <div className={wysiwygStyles.wysiwyg}>
          <h2 id={getHeadingId(sections[6])}>{sections[6]}</h2>
        </div>

        <FaqList faqs={entry.faqs} />

        <CTASection
          title="Try a third option on a real client round"
          description={CTA_DESCRIPTION}
          primaryButtonText="Start for Free"
          primaryButtonHref="/sign-up"
        />
      </ComparisonPageLayout>
    </>
  );
}
